import { FOCUS_RING } from './states';

/**
 * Sélecteur segmenté (refonte éditoriale).
 *
 * ### Le constat
 *
 * Le motif « un choix parmi deux ou trois, tous visibles » apparaît cinq fois
 * dans l'application — portée du journal (Mon journal / La famille), thème
 * (Clair / Sombre / Système), unités (Métrique / Impérial), lecture à voix
 * haute (Silencieuse / Lire les étapes), et le tri en lot. Il était rendu
 * cinq fois de la même façon : une rangée de `Button`, celui qui est
 * sélectionné en `variant="primary"`.
 *
 * Ce rendu a deux défauts, et le second est le grave :
 *
 * - **il crie.** Un bouton primaire est l'action que la page existe pour
 *   faire. Utilisé comme marqueur de sélection, il pose un aplat orange plein
 *   à côté d'un bouton à bordure : deux formes différentes pour deux options
 *   du même choix, alors que la seule différence est laquelle est active ;
 * - **il ment sur la nature du contrôle.** Deux boutons côte à côte sont deux
 *   actions indépendantes. Un choix exclusif est un groupe de boutons radio,
 *   et il doit s'annoncer comme tel : `role="radiogroup"`, navigation aux
 *   flèches, un seul arrêt de tabulation. C'était perdu.
 *
 * ### La forme
 *
 * Un rail en creux, une pastille claire sur l'option active. Le contraste
 * entre l'active et les autres vient de la **surface**, pas de la couleur —
 * c'est ce qui permet d'en poser trois côte à côte sans que l'écran vire à
 * l'orange.
 *
 * `role="radiogroup"` avec des `role="radio"` : la sémantique juste, et le
 * clavier qui va avec (flèches pour changer d'option, un seul `tabIndex` à 0).
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  /** Nom du choix, annoncé au lecteur d'écran (« Portée du journal »). */
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Occupe toute la largeur disponible — utile sur téléphone. */
  block?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  block = false,
  className = '',
}: SegmentedControlProps<T>): React.JSX.Element {
  const index = options.findIndex((option) => option.value === value);

  /**
   * Flèches : l'option suivante ou précédente, en boucle. C'est le
   * comportement attendu d'un groupe de boutons radio, et il n'existe pas
   * gratuitement sur des `<button>`.
   */
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    const pas = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1
        : 0;
    if (pas === 0) return;
    event.preventDefault();
    const suivant = options[(index + pas + options.length) % options.length];
    if (suivant !== undefined) onChange(suivant.value);
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={`inline-flex rounded-control bg-surface-sunken p-1 ${block ? 'w-full' : ''} ${className}`.trim()}
    >
      {options.map((option) => {
        const actif = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={actif}
            // Un seul arrêt de tabulation pour tout le groupe : on entre dans
            // le groupe, puis on choisit aux flèches.
            tabIndex={actif ? 0 : -1}
            onClick={() => { onChange(option.value); }}
            className={`min-h-touch-min flex-1 whitespace-nowrap rounded-[0.375rem] px-4 text-sm font-medium transition duration-(--duration-quick) ease-entrance ${FOCUS_RING} ${
              actif
                ? 'bg-surface text-foreground shadow-card'
                : 'text-foreground-secondary hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
